import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { catchError, forkJoin, lastValueFrom, map, Observable, Subscription } from 'rxjs';
import { PeriforOnChangeMessages, SignalRService } from '../../../shared/services/signal-r.service';
import { WellConfigService } from '../../../shared/services/well-config.service';
import { PoreAndFracService } from '../../services/pore-and-frac.service';
import { WellTypeService } from 'src/app/wellbore-inputs/services/well-type-datums.service';
import { PoreFrac, PoreFracUi } from '../../models/poreFrac.model';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';
import { FormArray, FormGroup } from '@angular/forms';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { Store } from '@ngneat/elf';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { GetFeetFromValue, GetPpgFromValue, GetPsiFromValue, GetValueFromFeet, GetValueFromPpg, GetValueFromPsi } from '../../shared/helpers/units.helper';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { CurrentAppIds } from 'src/app/core/components/user-admin-page/user-model';
import { LinearInterpSingle } from '../../shared/helpers/linear-interp-single.helper';
import { UnitsGridPasteTransform } from 'src/app/shared/components/units-grid-input/units-grid-input.models';
import {
  createPoreFracColumns,
  createPoreFracRow,
  ensureTrailingInputRow,
  extractPoreFracRows,
  populatePoreFracFormArray,
  transformPoreFracPasteRows
} from './pore-and-frac-grid.helper';

function calculatePressure(tvd: number, emw: number, uu: UserUnitsModel): number {
  if (!tvd || !emw) {
    return 0;
  }
  const depth = GetFeetFromValue(tvd, uu.longLengths);
  const mudWeight = GetPpgFromValue(emw, unitsLib[uu.density].symbol);
  return GetValueFromPsi((depth * mudWeight * 0.052), uu.pressure);
}

function calculateEmw(tvd: number, psi: number, uu: UserUnitsModel): number {
  if (!tvd || !psi) {
    return 0;
  }
  const depth = GetFeetFromValue(tvd, uu.longLengths);
  const pressure = GetPsiFromValue(psi, uu.pressure);
  return GetValueFromPpg((pressure / 0.052 / depth), unitsLib[uu.density].symbol);
}

@Component({
  selector: 'app-pore-and-frac',
  templateUrl: './pore-and-frac.component.html',
  standalone: false
})
export class PoreAndFracComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription = new Subscription();
  private _densityUnit: string;

  @Input() pressureType = '';

  public casingArray: { hanger: number, shoe: number, name: string, seqNumber: number }[] = [];
  public shoeDepth: number;
  public hangerMd: number;
  public annularFluidDensity: number;
  public pressures: PoreFrac[];
  public columnDefsPlot: any[];
  public mudlineDepth: number;
  public tablePlot: SelectItem[];
  public pressureEmw: SelectItem[];
  public isLoading = signal<boolean>(true);
  private currentString;
  private currentStringId: string;
  private currentStringHangerTvd: number;
  private currentStringShoeTvd: number;
  private _isOffshore: boolean;
  public _userUnits: UserUnitsModel;
  public lengthUnit: string;
  public poreFracRows = new FormArray<FormGroup>([]);
  public poreFracColumns = [];
  public readonly poreFracPasteTransform: UnitsGridPasteTransform = ({ startRowIndex, parsedRows, pasteDialogResult }) => transformPoreFracPasteRows(
    {
      startRowIndex,
      parsedRows,
      pasteDialogResult
    },
    {
      mudlineDepth: this.mudlineDepth,
      mudlineEquivalentMudWeight: Number(this.poreFracRows.at(0)?.get('equivalentMudWeight')?.value),
      mudlineTolerance: GetValueFromFeet(1, this.lengthUnit)
    }
  );
  public readonly poreFracPasteDialogData = { showPpFgDialog: true };
  public annotations: any[] = [];
  public shapes: any[] = [];
  public plot = {
    data: [],
    layout: {},
    config: {}
  };

  public yAxisTitle = '';
  public xAxisTitle = '';
  public plotNamePore: string;
  public plotNameFrac: string;
  public downloadPlotName = '';
  public plotTitle: string;
  public interpValueOptions: SelectItem[];
  public selectedInterpType = 'emw';
  public intepolatedValue: number;

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.poreFracStore = this._storeService.createStore(this.componentId, new PoreFracUi, withUIEntities<XyLinePlotUi>());
    this.tablePlotSelected.set(this.poreFracStore.state.tablePlotSelected || 'table');
    this.pressureEmwSelected.set(this.poreFracStore.state.pressureEmwSelected || 'emw');
    this.setPlotNames();
  }
  get componentId(): string {
    return this._componentId;
  }
  public poreFracStore: Store;

  public tablePlotSelected = signal<'table' | 'plot'>('table');
  public pressureEmwSelected = signal<'emw' | 'pressure'>('emw');

  constructor(
    private _poreAndFracService: PoreAndFracService,
    private _messenger: MediatorService,
    private _wellTypeService: WellTypeService,
    private _wellConfigService: WellConfigService,
    private _signalRService: SignalRService,
    private _trajectoryService: TrajectoryService,
    private _storeService: StoreService
  ) {
    this.columnDefsPlot = [
      { header: 'EMW', field: 'equivalentMudWeight' }
    ];
  }

  async ngOnInit(): Promise<void> {
    this._userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this._densityUnit = unitsLib[this._userUnits.density].symbol;
    this.lengthUnit = this._userUnits.longLengths;

    this.interpValueOptions = [
      { label: `EMW`, value: 'emw' },
      { label: `Pressure`, value: 'pressure' }
    ]

    this.plotTitle = this.pressureType === 'porePressure' ? 'Pore Pressure' : 'Fracture Gradient';
    this.downloadPlotName = this.pressureType === 'porePressure' ? 'pore_pressure_plot' : 'fracture_gradient_plot';
    this.setPlotNames();

    this.poreFracColumns = [...createPoreFracColumns(this._userUnits)];

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    this.getTubularString();

    this.tablePlot = [
      { label: 'Table', value: 'table' },
      { label: 'Plot', value: 'plot' }
    ];

    this.pressureEmw = [
      { label: 'EMW', value: 'emw' },
      { label: 'Pressure', value: 'pressure' }
    ];
  }

  signalRfunc(data: any) {

    if (data.action === PeriforOnChangeMessages.REFRESH_STRING_INPUTS ||
      data.action === PeriforOnChangeMessages.REFRESH_WELL_CONFIG) {
      const appIds = this._storeService.getSession<CurrentAppIds>(SessionStorageKeys.APP_IDS);
      this.currentStringId = appIds.tubularId;
      this.getTubularString();
    }
  }

  public readonly createPoreFracGridRow = (data?: Partial<PoreFrac>): FormGroup => {
    const row = createPoreFracRow(data);
    this.registerPoreFracRowCalculations(row);
    return row;
  };

  private populatePoreFracRows(): void {
    populatePoreFracFormArray(this.poreFracRows, this.pressures ?? [], (data?: Partial<PoreFrac>) => this.createPoreFracGridRow(data));
  }

  private registerPoreFracRowCalculations(row: FormGroup): void {
    this._subscriptions.add(row.get('equivalentMudWeight')?.valueChanges.subscribe(emw => {
      const pf = row.value as PoreFrac;
      const val = calculatePressure(pf.trueVerticalDepth, emw, this._userUnits);
      row.get('pressure')?.setValue(val, { emitEvent: false });
    }));

    this._subscriptions.add(row.get('pressure')?.valueChanges.subscribe(psi => {
      const pf = row.value as PoreFrac;
      const val = calculateEmw(pf.trueVerticalDepth, psi, this._userUnits);
      row.get('equivalentMudWeight')?.setValue(val, { emitEvent: false });
    }));
  }

  public readonly canDeletePoreFracRow = (_row: FormGroup, rowIndex: number): boolean => rowIndex > 0;

  public onPoreFracCellEdited(): void {
    ensureTrailingInputRow(this.poreFracRows, () => this.createPoreFracGridRow());
  }

  public onPoreFracRowDeleted(index: number): void {
    if (index > 0 && index < this.poreFracRows.length) {
      this.poreFracRows.removeAt(index);
    }
    this.onPoreFracGridCommit();
  }

  public onPoreFracRowsReset(): void {
    this.onPorePressuresChanged({ triggeredBy: { type: 'reset' }, dataRows: [], reload: true });
    this.populatePoreFracRows();
  }

  public onPoreFracPasteApplied(): void {
    this.recalculatePastedPoreFracRows();
    this.onPoreFracGridCommit('clipboard');
  }

  public onPoreFracGridCommit(triggeredByType = 'cellEdit'): void {
    ensureTrailingInputRow(this.poreFracRows, () => this.createPoreFracGridRow());
    this.onPorePressuresChanged({
      triggeredBy: { type: triggeredByType },
      dataRows: extractPoreFracRows(this.poreFracRows),
      reload: true
    });
  }

  private recalculatePastedPoreFracRows(): void {
    this.poreFracRows.controls.forEach((row) => {
      const pf = row.value as PoreFrac;
      if (pf.pressure && pf.pressure.toString() !== '-1' && pf.equivalentMudWeight && pf.equivalentMudWeight.toString() !== '-1') {
        return;
      }
      if (pf.equivalentMudWeight && pf.equivalentMudWeight.toString() !== '-1') {
        row.get('pressure')?.setValue(calculatePressure(pf.trueVerticalDepth, pf.equivalentMudWeight, this._userUnits), { emitEvent: false });
      }
      if (pf.pressure && pf.pressure.toString() !== '-1') {
        row.get('equivalentMudWeight')?.setValue(calculateEmw(pf.trueVerticalDepth, pf.pressure, this._userUnits), { emitEvent: false });
      }
    });
  }

  public onPorePressuresChanged(v: { triggeredBy: any, dataRows: PoreFrac[], reload: boolean }) {
    let defaultDensity = 8.6;
    switch (this._userUnits.density) {
      case 'g/L':
      case 'kg/m³': {
        defaultDensity = 1030.51;
        break;
      }
      case 'kg/l':
      case 'sg':
      case 'g/cm³':
        defaultDensity = 1.03;
        break;
      case 'g/m³':
        defaultDensity = 1030000;
        break;
      default: {
        break;
      }
    }

    if (v.triggeredBy.type === "clipboard" && (v.dataRows[0].equivalentMudWeight.toString() == '-1' || v.dataRows[1].equivalentMudWeight.toString() == '-1')) {
      return;
    }

    const valueToSave: PoreFrac[] = v.dataRows.length == 0 ? [{ trueVerticalDepth: this.mudlineDepth, equivalentMudWeight: defaultDensity, pressure: 0 }] : v.dataRows;

    if (this.pressureType === 'porePressure') {
      if (valueToSave[0].equivalentMudWeight.toString() !== '-1') {
        this._poreAndFracService.setPorePressures(valueToSave).subscribe(async () => {
          // Values calculated on client, no need to pull data from server.ks
        });
        this._messenger.publish(new DataSavedMessage('Pore Pressure'));
      } else {
        return;
      }
    } else {
      if (valueToSave[0].equivalentMudWeight.toString() !== '-1') {
        this._poreAndFracService.setFractureGradient(valueToSave).subscribe(async () => {
          // Values calculated on client, no need to pull data from server.
        });
        this._messenger.publish(new DataSavedMessage('Fracture Gradient'));
      } else {
        return;
      }
    }
  
      this.pressures = valueToSave;
      this.plotData();
  }

  public interpolate(e: any): void {
    const returnPressure = this.selectedInterpType === 'pressure' ? true : false;
    if (e.value < this.pressures[0].trueVerticalDepth) {
      this.intepolatedValue = returnPressure ? this.pressures[0].pressure : this.pressures[0].equivalentMudWeight;
      return;
    }
    if (e.value > this.pressures[this.pressures.length - 1].trueVerticalDepth) {
      this.intepolatedValue = returnPressure ? this.pressures[this.pressures.length - 1].pressure : this.pressures[this.pressures.length - 1].equivalentMudWeight;
      return;
    }
    this.intepolatedValue = LinearInterpSingle(e.value, this.pressures, this._userUnits, returnPressure);
  }

  public getTubularString() {
    this.isLoading.set(true);
    const sources: Observable<any>[] = [
      this.pressureType === 'porePressure' ? this._poreAndFracService.getPorePressures() : this._poreAndFracService.getFractureGradient(),
      this._wellConfigService.getTubulars(),
      this._wellTypeService.getWellType() as Observable<any>
    ];

    forkJoin(sources).pipe(
      map(([pressures, tubulars, wellType]) => {
        this.mudlineDepth = +(wellType.drillFloorElevation + wellType.waterDepth).toFixed(2);
        this._isOffshore = wellType.type == 'Subsea' || wellType.type == 'Platform';
        const appIds = this._storeService.getSession<CurrentAppIds>(SessionStorageKeys.APP_IDS);
        this.currentStringId = appIds.tubularId;
        this.currentString = tubulars.find(x => x.id === this.currentStringId);

        if (tubulars.length < 1) {
          this.pressures = pressures;
              this.populatePoreFracRows();
          this.isLoading.set(false);
        } else {
          this.casingArray = [];
          tubulars.forEach(async tubular => {
            if (tubular.type === 'Tubing' || tubular.type === 'Tieback') {
              return;
            }
            await this.getTvdData(tubular);
          });

          this.annularFluidDensity = this.currentString.annularFluid.state.nominalDensity > 0 ? this.currentString.annularFluid.state.nominalDensity : undefined;
          this.pressures = pressures;
          this.populatePoreFracRows();
        }
      }),
      catchError(err => {
        this.isLoading.set(false);
        return err;
      })).subscribe();
  }

  public async getTvdData(tubular: any) {
    const stringSections = tubular.stringSections;
    const bottomCasingDepth = stringSections[stringSections.length - 1].bottomMeasuredDepth;
    const topCasingDepth = tubular.hangerMd;

    const mds = [bottomCasingDepth, topCasingDepth, this.currentString?.hangerMd, this.currentString?.shoeMd];

    try {
      const tvds = await lastValueFrom(this._trajectoryService.getTvdsFromMds(mds));

      this.shoeDepth = tvds[0];              // Bottom casing depth Tvd
      this.hangerMd = tvds[1];               // Top casing depth Tvd
      this.currentStringHangerTvd = tvds[2]; // Hanger Md Tvd
      this.currentStringShoeTvd = tvds[3];   // Shoe Md Tvd

      this.casingArray.push({ hanger: this.hangerMd, shoe: this.shoeDepth, name: tubular.name, seqNumber: tubular.sequenceNumber });
      this.casingArray.sort((a, b) => (a.seqNumber > b.seqNumber ? 1 : -1));

      this.plotData();
    } catch (error) {
      console.error('Error fetching TVD values:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  plotData() {
    this.isLoading.set(true);
    const data = this.pressures;
    if (!data?.length) {
      this.isLoading.set(false);
      return;
    }

    const trueVerticalDepths = data.map(r => r.trueVerticalDepth);
    const pressures = data.map(r => r.pressure);
    const emws = data.map(r => r.equivalentMudWeight);

    // Find the index where the true vertical depth is greater than or equal to the mudline depth
    const mudlineIndex = trueVerticalDepths.findIndex(depth => depth >= this.mudlineDepth);
    const startIndex = mudlineIndex >= 0 ? mudlineIndex : 0;

    // Create new arrays that exclude depths and pressures below the mudline
    const filteredDepths = trueVerticalDepths.slice(startIndex);
    const filteredPressures = pressures.slice(startIndex);
    const filteredEmws = emws.slice(startIndex);

    // Add the mudline depth if necessary
    if (filteredDepths[0] > this.mudlineDepth) {
      filteredDepths.unshift(this.mudlineDepth);
      filteredPressures.unshift(0);
      filteredEmws.unshift(filteredEmws[0]);
    }

    const traceArray = this.columnDefsPlot.map(element => ({
      name: element.header,
      x: this.pressureEmwSelected() === 'emw'
        ? filteredEmws
        : filteredPressures,
      y: filteredDepths
    }));

    this.plot.data = traceArray;
    this.plotPressures(this.plot.data);
    this.isLoading.set(false);
  }

  tablePlotToggle(e): void {
    this.tablePlotSelected.set(e.value);
    this.poreFracStore.update(state => ({ ...state, tablePlotSelected: e.value }));
    this.setPlotNames();
    this.plotData();
  }

  pressureEmwToggle(e): void {
    this.pressureEmwSelected.set(e.value);
    this.poreFracStore.update(state => ({ ...state, pressureEmwSelected: e.value }));
    this.setPlotNames();
    this.plotData();
  }

  private setPlotNames(): void {
    this.plotNamePore = 'porePressurePlot - ' + this.pressureEmwSelected();
    this.plotNameFrac = 'fractureGradientPlot - ' + this.pressureEmwSelected();
  }

  plotPressures(plotData) {
    const plot = [];
    const data = plotData[0];

    const pressureData = {
      name: this.pressureEmwSelected() === 'emw' ? 'EMW ' : 'Pressure',
      x: data.x,
      y: data.y
    }

    plot.push(pressureData);

    if (this.pressureEmwSelected() === 'emw') {
      if (this.annularFluidDensity) {
        const annDensity = {
          name: "Annular Fluid Density",
          x: [this.annularFluidDensity, this.annularFluidDensity],
          y: [this.currentStringHangerTvd, this.currentStringShoeTvd]
        }

        plot.push(annDensity);
      }
    } else {
      const lastElement = plotData[0].x.length - 1;
      if (!plotData[0].x[lastElement]) {
        plotData[0].x.pop();
        plotData[0].y.pop();
      }
      const allPlottedXValues = plotData[0].x
        .filter((value: unknown): value is number => typeof value === 'number' && Number.isFinite(value));
      const largestPointExt = allPlottedXValues.length ? Math.max(...allPlottedXValues) : 0;
      const pressureIncrement = GetValueFromPsi(450, this._userUnits.pressure);
      const casingX = largestPointExt + pressureIncrement;
      const casingStartOffset = GetValueFromPsi(1, this._userUnits.pressure);

      const csgArrayLength = this.casingArray.length;
      let distance = casingStartOffset;
      for (let i = 0; i < csgArrayLength; i++) {
        const hanger = this.casingArray[i].hanger;
        const shoe = this.casingArray[i].shoe;
        const name = this.casingArray[i].name;

        const casing = {
          name: name,
          x: [casingX + distance, casingX + distance],
          y: [hanger, shoe],
          marker: {
            size: 10,
            symbol: ["line-ew", "triangle-up-dot"]
          },
          line: {
            color: '#999999'
          },
          hovertemplate: `TVD: %{y} ${this._userUnits.longLengths}`,
          showlegend: false
        }
        distance += pressureIncrement;

        plot.push(casing);
      }
    }

    this.shapes = [
      {
        type: "line",
        xref: "paper",
        x0: 0,
        y0: this.mudlineDepth,
        x1: 1,
        y1: this.mudlineDepth,
        line: {
          color: '#783F04'
        }
      }
    ];

    this.annotations = [
      {
        showarrow: false,
        text: this._isOffshore ? "Mudline" : "Ground Level",
        font: {
          size: 13,
          color: '#783F04'
        },
          xref: "paper",
          align: "right",
          x: 1,
          xanchor: "right",
          y: this.mudlineDepth,
          yanchor: "bottom",
      }
    ];

    this.plot.data = plot;
    this.xAxisTitle = this.pressureEmwSelected() === 'emw' ? `EMW (${this._densityUnit})` : `Pressure (${unitsLib[this._userUnits.pressure].symbol})`
    this.yAxisTitle = `TVD (${this._userUnits.longLengths})`;
  }

  ngOnDestroy() {
    this._subscriptions?.unsubscribe();
    this.signalRfunc = null;
  }

}
